/**
 * TypeScript shapes mirroring the Pydantic schemas in
 * `backend/app/api/v1/routes/contracts.py` and the timeline endpoint added
 * to `workflow.py`. Keep these in lockstep with the backend or the type
 * checker is lying to you.
 */
import { colors } from '../styles/theme';

export type ContractScope = 'general' | 'product';

/** Workflow state slugs from the contract_review_v1 definition. */
export type ContractCaseState =
  | 'pending_upload'
  | 'uploaded'
  | 'returned_to_client'
  | 'pending_mgr'
  | 'returned_to_agent'
  | 'approved'
  | 'rejected';

/** Presentational badge shown for each contract workflow state. */
export interface StateBadge {
  label: string;
  bg: string;
  fg: string;
}

const NOT_STARTED_BADGE: StateBadge = {
  label: 'Not started',
  bg: '#EEF2F7',
  fg: '#475569',
};

/** Map every workflow state to a presentational badge. Shared between
 *  the Contracts list page and the ContractCase detail page. */
export const STATE_BADGE: Record<ContractCaseState, StateBadge> = {
  pending_upload:     { label: 'Pending upload',      bg: colors.warningBg,  fg: colors.warning },
  uploaded:           { label: 'Under review',  bg: '#EEF2FF',         fg: '#4338CA'      },
  returned_to_client: { label: 'Action required',     bg: colors.dangerBg,   fg: colors.danger  },
  pending_mgr:        { label: 'Awaiting approval',   bg: '#EEF2FF',         fg: '#4338CA'      },
  returned_to_agent:  { label: 'Internal review',     bg: '#EEF2F7',         fg: '#475569'      },
  approved:           { label: 'Approved',            bg: colors.successBg,  fg: colors.success },
  rejected:           { label: 'Rejected',            bg: colors.dangerBg,   fg: colors.danger  },
};

export function badgeFor(state: ContractCaseState | null): StateBadge {
  return state ? STATE_BADGE[state] : NOT_STARTED_BADGE;
}

export interface ApplicableTemplate {
  template_id: string;
  slug: string;
  title: string;
  description: string | null;
  scope: ContractScope;
  current_version_id: string;
  current_version: number;
  expires_after_days: number | null;
  case_id: string | null;
  case_state: ContractCaseState | null;
}

export interface ClientMessage {
  /** One-paragraph summary curated by the staff reviewer. */
  summary: string;
  /** Bullet list of concrete issues the member must address. */
  issues: string[];
}

export interface ProductTerms {
  product_id: string;
  name: string;
  /** Null whenever funds could not answer for this vehicle (0073) —
   *  see `terms_available`. */
  currency_code: string | null;
  /** Decimal yield as string (e.g. "0.0950"). Render as percentage. */
  target_yield: string | null;
  term_days: number | null;
  min_investment: string | null;
  /** 1..5 — 1 = Conservative, 3 = Growth (current product taxonomy stops at 3). */
  risk_level: number | null;
  /** Redemption policy from funds' /terms (audit #138) — null fields mean
   *  funds did not send them; render as unavailable, never as a promise. */
  lockup_days: number | null;
  notice_period_days: number | null;
  redemption_frequency: string | null;
  /** funds' withdrawal-penalty method; null = early redemption refused today. */
  early_redemption_method: string | null;
  /** Decimal share of balance retained on early termination (e.g. "0.02") —
   *  only set when the penalty method is flat. */
  early_termination_retention_pct: string | null;
  /** Annual management fee in basis points from funds' catalog (audit #140).
   *  A real 0 means "no management fee"; null means funds could not answer. */
  management_fee_bps: number | null;
  /** False when funds could not answer for this vehicle — every other term
   *  above is null in that case. A null `term_days` while this is true means
   *  the product really is open-ended, not "unknown". */
  terms_available: boolean;
}

export interface CaseSummary {
  case_id: string;
  state_slug: ContractCaseState;
  status: string;
  template_id: string;
  template_version_id: string;
  template_title: string;
  template_scope: ContractScope;
  rendered_at: string | null;
  latest_signed_at: string | null;
  latest_signed_doc_id: string | null;
  can_upload: boolean;
  client_message: ClientMessage | null;
  /** Snapshot of product terms — populated only when template_scope='product'. */
  product_terms: ProductTerms | null;
  /** The client's KYC legal name, the one the signature is made in: pre-filled, never typed twice. */
  signer_name: string;
}

export interface DownloadUrlResponse {
  download_url: string;
  expires_in_seconds: number;
  rendered_at: string | null;
}

export interface UploadPresignResponse {
  upload_url: string;
  object_key: string;
  sequence: number;
  expires_in_seconds: number;
}

export interface UploadConfirmResponse {
  case_id: string;
  document_id: string;
  new_state_id: string;
  object_key: string;
  file_hash: string;
  size_bytes: number;
}

/** What the signing screen of a US series needs (`GET /contracts/cases/{id}/us-offering`):
 *  the regime that sells it to this person, the DRAFT legend line, and either the Regulation S
 *  certification text (a Mexican resident) or the Rule 506(b) questionnaire (a US person). */
export interface UsOfferingInfo {
  regime: 'reg_d_506b' | 'reg_s_cat3';
  legend: { version: string; text: string };
  /** A Mexican SAPI bought from outside Mexico: the shares cannot be offered publicly in Mexico. */
  abroad_legend?: { version: string; text: string };
  certification?: { version: string; text: string };
  categories?: { key: string; statement: string }[];
  prefill_category?: string | null;
  /** Rule 506(b): the resale disclosure (502(d)(2)) and the own-account statement (502(d)(1)). */
  resale?: { version: string; text: string };
  own_account?: { version: string; text: string };
}

/** What the person gives at the signature of a US series. */
export interface UsOfferingAnswers {
  purchaserCertification?: boolean;
  accreditationCategory?: string | null;
  affirmsAccreditation?: boolean;
  acquiringForOwnAccount?: boolean;
}

export interface SignContractResponse {
  case_id: string;
  document_id: string;
  reference_number: string;
  new_state_id: string;
  signed_at: string;
  size_bytes: number;
}

export type AssistanceType = 'await_contact' | 'schedule_call';
export type AssistanceWindow = 'morning' | 'afternoon' | 'evening';

export interface AssistanceRequestBody {
  type: AssistanceType;
  preferred_window?: AssistanceWindow | null;
  /** ISO date YYYY-MM-DD; only used for schedule_call. */
  preferred_date?: string | null;
  notes?: string | null;
}

/** Single event from GET /workflow/cases/{id}/timeline. */
export interface TimelineEvent {
  id: number;
  event: string;
  from_state: string | null;
  to_state: string;
  actor_role: string;
  created_at: string;
  /** Internal staff notes — only returned to staff viewers; null for clients. */
  notes: string | null;
  /** Staff identity — only returned to staff viewers; null for clients. */
  triggered_by: string | null;
  triggered_by_email: string | null;
  /** Curated client-facing message; visible to all viewers when present. */
  client_visible_notes: ClientMessage | null;
}
